/**
 * File-local multi-ref click-outside hook for the sidebar.
 *
 * Replicates the three inline `document.addEventListener('mousedown', …)`
 * effects in layout/Sidebar.tsx byte-for-byte in behavior:
 *  - the listener is added ONLY while `enabled` is truthy and removed on
 *    cleanup (open-gated subscription);
 *  - it fires `onOutside` only when EVERY provided ref is present (non-null)
 *    AND none of them contains the click target.
 *
 * The single-ref menus pass one ref; the chats gear menu passes two
 * (menu + button), matching the original `(menu && !menu.contains) && (btn &&
 * !btn.contains)` predicate.
 *
 * File-local on purpose (rules 6/9): only this file proves the pattern. Promote
 * to common/ once a second file needs it.
 */

import { useEffect } from 'react';

/**
 * Read-only ref view. Typed covariantly (read-only `current`) so callers can
 * pass `RefObject<HTMLDivElement | null>`, `RefObject<HTMLButtonElement | null>`,
 * etc. without the invariance error that a mutable `RefObject<HTMLElement>`
 * parameter would trigger.
 */
type ReadonlyElementRef = { readonly current: HTMLElement | null };

export function useClickOutside(
  refs: ReadonlyArray<ReadonlyElementRef>,
  onOutside: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideAll = refs.every(
        (ref) => ref.current && !ref.current.contains(event.target as Node),
      );
      if (isOutsideAll) {
        onOutside();
      }
    };

    if (enabled) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [enabled]);
}
